import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../config/api';
import { useLanguage } from '../contexts/LanguageContext';

const Footer = () => {
  const { t, language } = useLanguage();
  const [socialLinks, setSocialLinks] = useState({});
  
  useEffect(() => {
    axios.get(`${API}/settings/season`).then(res => setSocialLinks(res.data)).catch(() => {});
  }, []);
  
  const youtubeUrl = socialLinks.youtube_url || '';
  
  return (
    <footer className="bg-[#1E3A5F] text-white mt-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <span className="font-bold text-lg">W</span>
              </div>
              <span className="font-display font-bold text-xl">WBLA</span>
            </div>
            <p className="text-slate-300 text-sm">{t('footerTagline')}</p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">{t('quickLinks')}</h4>
            <div className="space-y-2 text-slate-300 text-sm">
              <Link to="/about" className="block hover:text-white">{t('aboutUs')}</Link>
              <Link to="/schedule" className="block hover:text-white">{t('schedule')}</Link>
              <Link to="/teams" className="block hover:text-white">{t('teams')}</Link>
              <Link to="/results" className="block hover:text-white">{t('results')}</Link>
              <Link to="/scoreboard" className="block hover:text-white">{t('scoreboard')}</Link>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">{t('legal')}</h4>
            <div className="space-y-2 text-slate-300 text-sm">
              <Link to="/privacy" className="block hover:text-white">{t('privacyPolicy')}</Link>
              <Link to="/terms" className="block hover:text-white">{t('termsOfUse')}</Link>
              <Link to="/cookies" className="block hover:text-white">{language === 'gr' ? 'Πολιτική Cookies' : 'Cookie Policy'}</Link>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">{t('followUs')}</h4>
            <div className="flex space-x-3">
              <a href="https://www.facebook.com/share/1FkJdSWQAv/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition" data-testid="footer-facebook">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.instagram.com/womensbasketleague_gr?igsh=MXFkNjY1eTRqMjBlaw%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition" data-testid="footer-instagram">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C16.67.014 16.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href={youtubeUrl || 'https://www.youtube.com'} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition" data-testid="footer-youtube">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 mt-8 pt-8 text-center text-slate-400 text-sm">
          © 2026 Women's Basketball League Athens. {t('allRights')}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
